import Styles from './accountPanel.module.css';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { collection, getDocs, getFirestore, orderBy, query, where } from 'firebase/firestore';
import { IoClose } from 'react-icons/io5';
import PostLikeHeart from 'src/salon/package/postLikeHeart/index';
import MyCardTab from '../15.evaluation/detail/MyCardTab';
import { pickupWorkImageUrl } from '../15.evaluation/detail/studentProfile';

type StudentWork = { id: string; postName: string; companyLikes: string[] };

type Props = {
  postId: string;
  postUid: string;
  companyLikes: string[];
  onClose: () => void;
  // どの作品(postId)のいいねが変わったかも渡す。上部に出している作品は
  // 下の「作品撮り」から切り替えられるため、変更対象は selectedPost とは限らない。
  onToggled: (postId: string, nextLikes: string[]) => void;
};

// 「アカウント情報を見る」ボタンから開く右側パネル。既存の学生情報の閲覧(My Card =
// src/salon/desktop/15.evaluation/detail/MyCardTab.tsx、応募者評価画面と共通の
// 既存コンポーネント)といいねの2つだけに絞る。新しいプロフィール項目やダミーの
// 自己紹介・タグは追加しない。サロン側から遷移できる既存の学生詳細ページは無いため
// 「詳細を見る」リンクは設置しない。
const StudentAccountPanel: React.FC<Props> = ({
  postId, postUid, companyLikes, onClose, onToggled,
}) => {

  const [ works, setWorks ] = useState<StudentWork[]>([]);
  // 上部に大きく出している作品。初期は「アカウント情報を見る」を押した作品。
  // 下の「作品撮り」のサムネイルを押すと、その作品に差し替わる。
  const [ activeId, setActiveId ] = useState(postId);
  // いいねのローカル上書き（トグルした瞬間に上部表示へ反映するため）。
  const [ likesById, setLikesById ] = useState<Record<string, string[]>>({});
  const bodyRef = useRef<HTMLDivElement>(null);

  // 別の学生のパネルを開き直したら、上部表示とローカル上書きをリセットする。
  useEffect(() => {
    setActiveId(postId);
    setLikesById({});
  }, [postId]);

  // MyCardTabの「Pick Up Works」は学生が任意で選んだ一部(pickupWorkIds)のみで、
  // 未設定なら何も出ない。ここでは14.studentWorksと同じ posts コレクション
  // (post_uid/post_student)に対する既存クエリで、この学生が実際に投稿した
  // 作品撮り全件を取得し、あれば表示する。
  useEffect(() => {
    let cancelled = false;
    if (!postUid) {
      setWorks([]);
      return;
    }
    (async () => {
      const db = getFirestore();
      const snap = await getDocs(query(
        collection(db, 'posts'),
        where('post_uid', '==', postUid),
        where('post_student', '==', true),
        orderBy('post_time', 'desc'),
      ));
      if (cancelled) return;
      setWorks(snap.docs.map((d) => {
        const data = d.data();
        return {
          id: d.id,
          postName: data.post_name ?? '',
          companyLikes: Array.isArray(data.companyLikes) ? data.companyLikes : [],
        };
      }));
    })().catch((error) => {
      console.error('studentWorks account panel fetch failed:', error);
    });
    return () => { cancelled = true; };
  }, [postUid]);

  const worksLikes = useMemo(() => {
    const map: Record<string, string[]> = {};
    works.forEach((w) => { map[w.id] = w.companyLikes; });
    return map;
  }, [works]);

  // ある作品の現在のいいね配列。ローカル上書き > (元postなら)propの値 > 一覧取得値。
  function likesFor(id: string): string[] {
    return likesById[id] ?? (id === postId ? companyLikes : worksLikes[id]) ?? [];
  }

  function handleToggled(id: string, nextLikes: string[]) {
    setLikesById((prev) => ({ ...prev, [id]: nextLikes }));
    onToggled(id, nextLikes);
  }

  function selectWork(id: string) {
    setActiveId(id);
    bodyRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  }

  const activeName = works.find((w) => w.id === activeId)?.postName ?? '';
  const otherWorks = works.filter((w) => w.id !== activeId);

  return (
    <div className={Styles.drawer}>
      <div className={Styles.header}>
        <h3 className={Styles.headerTitle}>アカウント情報</h3>
        <button type="button" className={Styles.closeBtn} onClick={onClose}>
          <IoClose size={22} />
        </button>
      </div>

      <div className={Styles.body} ref={bodyRef}>
        {/* 上部に大きく出している作品とそのいいね。下の「作品撮り」を押すと
            ここが差し替わり、その作品に対していいねできる。 */}
        <div className={`${Styles.section} ${Styles.heroSection}`}>
          <img
            className={Styles.heroImage}
            src={pickupWorkImageUrl(activeId)}
            alt={activeName || '美容学生作品撮り'}
          />
          <div className={Styles.likeRow}>
            <PostLikeHeart
              key={activeId}
              postId={activeId}
              postUid={postUid}
              companyLikes={likesFor(activeId)}
              onToggled={(next) => handleToggled(activeId, next)}
              variant="button"
            />
          </div>
        </div>

        <div className={Styles.section}>
          <h5 className={Styles.sectionHeading}>プロフィール</h5>
          <MyCardTab uid={postUid} hideSns flat hideAboutMe hidePickupWorks hideSchoolLine />
        </div>

        {otherWorks.length > 0 && (
          <div className={Styles.section}>
            <h5 className={Styles.sectionHeading}>作品撮り</h5>
            <div className={Styles.worksGrid}>
              {otherWorks.map((w) => (
                <button
                  type="button"
                  key={w.id}
                  className={Styles.workButton}
                  onClick={() => selectWork(w.id)}
                >
                  <img
                    className={Styles.workImage}
                    src={pickupWorkImageUrl(w.id)}
                    alt={w.postName || '美容学生作品撮り'}
                  />
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StudentAccountPanel;
